import * as React from 'react';
import { Button } from 'antd';
import { Link } from 'react-router-dom';
import { Redirect } from 'react-router';
import moment from 'moment';
import styled from 'styled-components';
import { DownloadOutlined, FilterOutlined, MailOutlined } from '@ant-design/icons';

import { UploadPaths } from 'features/upload/models/uploadModels';
import { PaginatedTable } from 'features/pagination/components/PaginatedTable';
import { PaginationArchivedFilter } from 'features/pagination/components/PaginationArchivedFilter';
import { generateColumns, DownloadFileFormats } from 'common/helpers/utils';
import { DownloadAll } from 'features/download/models/downloadModel';
import { PaginationView } from 'features/pagination/models/paginationModel';
import { getBenefitsDashboardSuccess } from 'features/benefit/actions/benefitActions';
import {
    BenefitDashboardRow,
    BenefitDashboardTab,
    benefitDashboardTabFilters,
    benefitDashboardTabOrder,
    EditBenefitPaths,
} from 'features/benefit/models/benefitModels';
import { EditContractPaths } from 'features/contract/models/contractModels';
import { EditPaths } from 'features/edit/models/editModel';
import { ContentMenu, PaddedSection } from 'common/components';
import { StatusPill } from 'common/components/StatusPill';
import { TableFiltersBar } from 'common/components/TableFiltersBar';
import { FilterDrawer } from 'common/components/FilterDrawer';
import { SendCustomEmailModal } from 'features/benefit/components/SendCustomEmailModal';
import { defaultPagination } from 'config/variables';

import { BenefitsDashboardFilter } from './BenefitsDashboardFilter';
import { BenefitsDashboardContainerProps } from '../containers/BenefitsDashboardContainer';
import { DashboardPaths } from '../models/dashboardModel';

/* The sub menu and the section menu above it both render flush, so a small gap keeps
   them reading as two separate bars. */
const SubMenuWrapper = styled.div`
    margin-top: 12px;
`;

const SendEmailButtonWrapper = styled.div`
    margin-top: 16px;
`;

/* Wraps so the row stays reachable on a phone; see the mobile button-row fix. */
const DownloadButtonWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
`;

/* The email arrays only exist on API versions that include the CFS-1041 backend change,
   so a row from an older backend must render as '-' rather than crash the view. */
const sortEmails = (emails: string[] | null | undefined) =>
    emails ? [...emails].sort((a, b) => a.localeCompare(b)) : [];

const joinEmails = (emails: string[]) => (emails.length ? emails.join(', ') : '-');

const benefitsDashboardColumns = [
    {
        title: 'Benefit',
        key: 'outcome',
        render: (outcome: string, { contract, id, referenceNumber }: BenefitDashboardRow) => (
            <Link
                to={`${EditPaths.Contract}/${contract}/benefits/${id}${EditBenefitPaths.Details}`}
            >
                {`${outcome} | ${referenceNumber}`}
            </Link>
        ),
    },
    {
        title: 'Label',
        key: 'label',
        render: (label: string | null) => label || '-',
    },
    {
        title: 'Supplier',
        key: 'supplierOrganisation',
        render: (supplierOrganisation: string | null) => supplierOrganisation || '-',
    },
    {
        title: 'Submitters',
        key: 'contributors',
        // Contributors, then supplier and delivery partner executives, alphabetical within
        // each group.
        render: (contributors: string[], { suppliers, deliveryPartners }: BenefitDashboardRow) =>
            joinEmails([
                ...sortEmails(contributors),
                ...sortEmails(suppliers),
                ...sortEmails(deliveryPartners),
            ]),
    },
    {
        title: 'Completion Date',
        key: 'finalDeliveryDate',
        render: (finalDeliveryDate: string | null) => finalDeliveryDate || '-',
    },
    {
        title: 'Closure Date',
        key: 'completionDate',
        render: (completionDate: string | null) => completionDate || '-',
    },
    {
        title: 'Status',
        key: 'status',
        render: (status: string) => <StatusPill status={status} />,
    },
    {
        title: 'Project',
        key: 'contractTitle',
        render: (
            contractTitle: string,
            { contract, contractReferenceNumber }: BenefitDashboardRow,
        ) => (
            <Link to={`${EditPaths.Contract}/${contract}${EditContractPaths.ContractDetails}`}>
                {`${contractTitle} | ${contractReferenceNumber}`}
            </Link>
        ),
    },
    {
        title: 'Project Managers',
        key: 'projectManagers',
        render: (projectManagers: string[]) => joinEmails(sortEmails(projectManagers)),
    },
];

const tabMenuLinks = benefitDashboardTabOrder.map((tab) => ({
    key: `${DashboardPaths.Benefits}/${tab}`,
    label: benefitDashboardTabFilters[tab].label,
}));

interface BenefitsDashboardState {
    selectedBenefitIds: string[];
    isFilterDrawerOpen: boolean;
    isCustomEmailModalOpen: boolean;
}

export class BenefitsDashboard extends React.Component<
    BenefitsDashboardContainerProps,
    BenefitsDashboardState
> {
    public state: BenefitsDashboardState = {
        selectedBenefitIds: [],
        isFilterDrawerOpen: false,
        isCustomEmailModalOpen: false,
    };

    constructor(props: BenefitsDashboardContainerProps) {
        super(props);

        // First open in the session (no saved filters for this view yet): default the
        // completion date range to a year either way, matching the other Measure
        // dashboards (CFS-1009). The forward bound keeps benefits that are still due on
        // the Outstanding tab; clearing filters stays cleared for the rest of the session.
        if (!props.filter) {
            props.saveFilters({
                paginatedView: PaginationView.BenefitsDashboard,
                values: {
                    minEndDate: moment().subtract(12, 'months').format(),
                    maxEndDate: moment().add(12, 'months').format(),
                },
            });
        }

        // Must run before the table mounts: children mount before parents, so the request
        // PaginatedTable fires from its own componentDidMount would otherwise reach the epic
        // before the first tab's parameters were saved and fetch every benefit unfiltered.
        this.applyTabFilters(props.activeTab);
    }

    public componentDidUpdate(prevProps: BenefitsDashboardContainerProps): void {
        // The tab lives in the URL now, so switching tabs arrives as a prop change.
        if (prevProps.activeTab !== this.props.activeTab) {
            this.applyTabFilters(this.props.activeTab);
            this.setState({ selectedBenefitIds: [] });
        }
    }

    /**
     * Filters are stored per paginated view and merged on save, so the tab parameters have
     * to be written before the table fetches. Any filters the user added stay in place
     * because only the tab's own keys are overwritten.
     */
    private applyTabFilters(activeTab: BenefitDashboardTab) {
        this.props.saveFilters({
            paginatedView: PaginationView.BenefitsDashboard,
            values: benefitDashboardTabFilters[activeTab].filters,
            path: `${DashboardPaths.Benefits}/${activeTab}`,
        });
    }

    private refreshTable = () =>
        this.props.paginationRequest({
            view: PaginationView.BenefitsDashboard,
            pagination: { current: defaultPagination.firstPage },
            paginationSuccessCallback: getBenefitsDashboardSuccess,
        });

    /**
     * Saving or clearing filters from the drawer overwrites the whole stored filter set for
     * this view, so the tab's status and ordering are re-applied before refetching.
     */
    private applyDrawerFilters = () => {
        this.setState({ isFilterDrawerOpen: false });
        this.applyTabFilters(this.props.activeTab);
        this.refreshTable();
    };

    private handleSaveFilters = (filters: Parameters<typeof this.props.saveFilters>[0]) => {
        this.props.saveFilters(filters);
        this.applyDrawerFilters();
    };

    private handleClearFilters = (view: PaginationView) => {
        // Returns the action so ResetButton's expected signature is satisfied.
        const action = this.props.clearFilters(view);

        this.applyDrawerFilters();

        return action;
    };

    /*
     * Mirrors DownloadBenefits.handleSubmit against the same export endpoint.
     *
     * paginatedView is BenefitsDashboard, so the epic reads THIS view's saved
     * filters rather than the Download Benefits page's.
     *
     * benefitType is not sent, matching Download Benefits with no type selected.
     * The endpoint still defaults benefit_type to "templated", but only evidence
     * and feedback exports apply it: this summary export includes templated and
     * unique benefits alike, selected rows included.
     *
     * "Download selected" sends the accumulated ids. PaginatedTable forces antd's
     * preserveSelectedRowKeys, so that set survives paging and selections made on
     * later pages are included. "Download all" ignores ids: the backend re-runs the
     * filtered queryset, so it covers the whole filtered view, not just this page.
     */
    private handleDownload = ({ downloadAll = false }: DownloadAll = {}): void => {
        this.props.getBenefitsCSVRequest({
            ids: downloadAll ? [] : this.state.selectedBenefitIds.map(Number),
            fileFormat: DownloadFileFormats.XLSX,
            downloadAll,
            exportType: 'summary',
            paginatedView: PaginationView.BenefitsDashboard,
        });
    };

    private onSelectChange = (selectedBenefitIds: string[]) =>
        this.setState({ selectedBenefitIds });

    public render(): JSX.Element {
        const { loading, isUserTenant, isUserAdmin, benefits, activeTab, filter, isFetchingCSV } =
            this.props;
        const { selectedBenefitIds, isFilterDrawerOpen, isCustomEmailModalOpen } = this.state;

        if (!loading && !isUserTenant) {
            return <Redirect to={UploadPaths.PendingEvidence} />;
        }

        return (
            // The sub menu sits between the section menu and the page header, matching the
            // rest of the app.
            <>
                <SubMenuWrapper>
                    <ContentMenu menuLinks={tabMenuLinks} small />
                </SubMenuWrapper>
                <PaddedSection header="Benefits">
                    <TableFiltersBar
                        leftButtons={[
                            <Button
                                type="primary"
                                icon={<FilterOutlined />}
                                onClick={() => this.setState({ isFilterDrawerOpen: true })}
                            >
                                Filters
                            </Button>,
                        ]}
                        filters={[
                            /* Only Measure dashboard whose endpoint takes
                               show_archived; the other four are served by
                               DashboardViewSet, whose filterset does not. */
                            <PaginationArchivedFilter
                                paginatedView={PaginationView.BenefitsDashboard}
                                refreshTable={this.refreshTable}
                            />,
                        ]}
                    />
                    <FilterDrawer
                        visible={isFilterDrawerOpen}
                        onClose={() => this.setState({ isFilterDrawerOpen: false })}
                        title="Filter Benefits"
                    >
                        {isFilterDrawerOpen && (
                            <BenefitsDashboardFilter
                                formInitial={filter}
                                saveFilters={this.handleSaveFilters}
                                clearFilters={this.handleClearFilters}
                            />
                        )}
                    </FilterDrawer>
                    <SendCustomEmailModal
                        visible={isCustomEmailModalOpen}
                        onClose={() => this.setState({ isCustomEmailModalOpen: false })}
                        benefitIds={selectedBenefitIds.map((id) => parseInt(id, 10))}
                        canTargetAllRoles={isUserAdmin && isUserTenant}
                        onSent={() => this.setState({ selectedBenefitIds: [] })}
                    />
                    {/*
                     * Pagination state (current page, total, isFetching) is global, so only the
                     * active tab's table is mounted. Keying it by tab remounts on every switch,
                     * which resets to page one and refetches with the new tab parameters.
                     */}
                    <PaginatedTable
                        key={activeTab}
                        data={benefits}
                        columns={generateColumns(benefitsDashboardColumns)}
                        emptyText="No benefits found"
                        view={PaginationView.BenefitsDashboard}
                        paginationSuccessCallback={getBenefitsDashboardSuccess}
                        rowSelection={{
                            selectedRowKeys: selectedBenefitIds,
                            onChange: this.onSelectChange,
                        }}
                    />
                    {/* Below the table on the left, matching Edit My/All Benefits. */}
                    <SendEmailButtonWrapper>
                        <Button
                            type="primary"
                            icon={<MailOutlined />}
                            disabled={!selectedBenefitIds.length}
                            onClick={() => this.setState({ isCustomEmailModalOpen: true })}
                        >
                            Send Custom Email
                        </Button>
                    </SendEmailButtonWrapper>
                    <DownloadButtonWrapper>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={!selectedBenefitIds.length || isFetchingCSV}
                            loading={isFetchingCSV}
                            onClick={() => this.handleDownload()}
                        >
                            {`Download selected ${
                                selectedBenefitIds.length ? `(${selectedBenefitIds.length})` : ''
                            }`}
                        </Button>
                        <Button
                            type="default"
                            icon={<DownloadOutlined />}
                            disabled={isFetchingCSV}
                            loading={isFetchingCSV}
                            onClick={() => this.handleDownload({ downloadAll: true })}
                        >
                            Download all
                        </Button>
                    </DownloadButtonWrapper>
                </PaddedSection>
            </>
        );
    }
}
